import React, { useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FiLoader, FiSend } from 'react-icons/fi';
import { MagicLink } from '@/components/custom/magic-link';
import { searchTextLink } from '@/services/linkable.js';
import api from '@/services/api';

export function PostShare({ selectedClassroomId, onPostCreated }) {
  const { t } = useTranslation();
  const client = useClient();
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [magicLinks, setMagicLinks] = useState([]);
  const [magicLinkRemoved, setMagicLinkRemoved] = useState(false);
  const [checkingLink, setCheckingLink] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(true);

  const getLinkInfo = async url => {
    try {
      const response = await api.get(`link_infos/search?url=${url}`);
      return response.data;
    } catch (error) {
      console.error(error);
      setCheckingLink(false);
    }
  };

  const checkHasLink = e => {
    if (!magicLinkRemoved) {
      const bodyText = e.target.value + ' ';
      const links = searchTextLink(bodyText);
      if (links.length > 0) {
        setCheckingLink(true);
        setMagicLinkRemoved(true);
        getLinkInfo(links[0]).then(magicLink => {
          setMagicLinks([magicLink]);
          setCheckingLink(false);
        });
      }
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!text.trim() || isSubmitting) return;

    setIsSubmitting(true);

    const params = {
      data: {
        type: 'Post',
        attributes: {
          body: text.trim(),
          'classroom-id': selectedClassroomId === 'blank' || !selectedClassroomId ? null : selectedClassroomId
        },
        relationships: { 'magic-links': { data: showMagicLink ? magicLinks : [] } }
      }
    };

    try {
      const { data, error } = await client.mutate('posts', undefined, { body: JSON.stringify(params) });
      if (error) {
        toast.error(error.title);
      } else {
        setText('');
        setMagicLinks([]);
        setMagicLinkRemoved(false);
        setShowMagicLink(true);
        toast.success(t('toast.successSendMessage'));
        onPostCreated(data);
      }
    } catch (e) {
      toast.error(t('toast.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-2 rounded-2xl border border-neutral-lightest bg-white p-3 shadow-card"
    >
      <div className="flex items-end gap-2">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyUp={e => checkHasLink(e)}
          onBlur={e => checkHasLink(e)}
          onPaste={e => checkHasLink(e)}
          placeholder="Compartilhe algo com a turma..."
          aria-label="Compartilhe algo com a turma"
          rows={2}
          className="min-h-[44px] grow resize-none rounded-xl border border-neutral-lighter px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={!text.trim() || isSubmitting}
          className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-extrabold text-white transition-transform active:translate-y-px disabled:opacity-50"
          style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
        >
          {isSubmitting || checkingLink ? <FiLoader className="h-3.5 w-3.5 animate-spin" /> : <FiSend className="h-3.5 w-3.5" />}
          Postar
        </button>
      </div>

      {showMagicLink &&
        magicLinks.map((magicLink, index) => (
          <MagicLink
            showClose={true}
            setShowMagicLink={setShowMagicLink}
            key={index}
            magicLink={magicLink.attributes}
            className="mt-2"
          />
        ))}
    </form>
  );
}
